/**
 * QueryFileUpload Component
 *
 * Main component for uploading and managing YAML query configuration files
 */

import React, { useState, useCallback } from 'react';
import { CheckCircle2, XCircle, Loader2, File, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useQueryFileValidation } from '@/hooks/useQueryFileValidation';
import { QueryFileDropzone } from './QueryFileDropzone';
import { QueryFilePreview } from './QueryFilePreview';
import type { ConnectorType, QueryFile } from '@/types/connectors';

interface QueryFileUploadProps {
  connectorType: ConnectorType;
  value?: QueryFile | null;
  onChange: (file: QueryFile | null) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

type UploadState = 'empty' | 'uploading' | 'valid' | 'invalid' | 'has-file';

/**
 * Main QueryFileUpload Component
 */
export const QueryFileUpload: React.FC<QueryFileUploadProps> = ({
  connectorType,
  value,
  onChange,
  error,
  disabled = false,
  className,
}) => {
  // Only adopt a value that is a real QueryFile (carries a `queries` array).
  // A truthy-but-malformed value — e.g. a connector's raw
  // `settings.queries` (a dict/array of inline query defs, not a QueryFile)
  // leaked in via the field-name collision (#968) — must NOT flip us to
  // 'has-file', or the `.queries.length` reads below throw. Computed once as
  // the lazy initial state so the very first render already reflects the
  // incoming `value` (the old mount-time effect run is no longer needed).
  const [uploadState, setUploadState] = useState<UploadState>(() =>
    value && Array.isArray(value.queries) ? 'has-file' : 'empty'
  );
  const [currentFile, setCurrentFile] = useState<QueryFile | null>(value || null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { validateFile } = useQueryFileValidation();

  // Sync with external value changes made AFTER mount (e.g. the parent form
  // loads a saved query file asynchronously). Adjust state during render
  // (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
  // via a previous-value mirror: `prevValue` converges to `value` on the very
  // next render, so this fires exactly once per external `value` change.
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (value && Array.isArray(value.queries)) {
      setCurrentFile(value);
      setUploadState('has-file');
    } else if (!currentFile) {
      setUploadState('empty');
    }
  }

  /**
   * Handle file drop/selection
   */
  const handleFileDrop = useCallback(
    async (file: File) => {
      setUploadState('uploading');
      setValidationErrors([]);

      try {
        const queryFile = await validateFile(file, connectorType);
        setCurrentFile(queryFile);
        setUploadState('valid');
        onChange(queryFile);
      } catch (error: any) {
        const errorMessage = error.message || 'File validation failed';
        const errors = errorMessage.split('\n').filter(Boolean);
        setValidationErrors(errors);
        setUploadState('invalid');
        setCurrentFile({ filename: file.name, content: '', queries: [] });
      }
    },
    [validateFile, connectorType, onChange]
  );

  /**
   * Handle replace file
   */
  const handleReplaceFile = useCallback(() => {
    setUploadState('empty');
    setCurrentFile(null);
    setValidationErrors([]);
  }, []);

  /**
   * Handle remove file
   */
  const handleRemoveFile = useCallback(() => {
    setUploadState('empty');
    setCurrentFile(null);
    setValidationErrors([]);
    onChange(null);
  }, [onChange]);

  /**
   * Handle cancel (after error)
   */
  const handleCancel = useCallback(() => {
    setUploadState(value ? 'has-file' : 'empty');
    setCurrentFile(value || null);
    setValidationErrors([]);
  }, [value]);

  /**
   * Render based on current state
   */
  const renderContent = () => {
    switch (uploadState) {
      case 'empty':
        return (
          <>
            <QueryFileDropzone onFileDrop={handleFileDrop} disabled={disabled} />
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Query files define which data to extract from your connector. You can upload one later if needed.
              </AlertDescription>
            </Alert>
          </>
        );

      case 'uploading':
        return (
          <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-lg">
            <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" aria-label="Validating" />
            <p className="text-sm text-muted-foreground">Validating query file...</p>
          </div>
        );

      case 'valid':
      case 'has-file':
        // If no file, no `queries` array, or no queries, show the dropzone
        // instead of nothing. The `Array.isArray` guard keeps a malformed
        // value (see the sync effect above) from crashing on `.length`. (#968)
        if (!currentFile || !Array.isArray(currentFile.queries) || currentFile.queries.length === 0) {
          return (
            <>
              <QueryFileDropzone onFileDrop={handleFileDrop} disabled={disabled} />
              <Alert className="mt-4">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Query files define which data to extract from your connector. You can upload one later if needed.
                </AlertDescription>
              </Alert>
            </>
          );
        }

        const isNewUpload = uploadState === 'valid';
        const uploadedTime = currentFile.uploadedAt
          ? formatUploadTime(currentFile.uploadedAt)
          : undefined;

        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 border border-border rounded-lg bg-muted/30">
              <div className="flex-shrink-0 mt-0.5">
                {isNewUpload ? (
                  <CheckCircle2 className="h-5 w-5 text-success" aria-label="Success" />
                ) : (
                  <File className="h-5 w-5 text-muted-foreground" aria-label="File" />
                )}
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-sm font-medium">{currentFile.filename}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isNewUpload ? (
                      <span className="text-success font-medium">
                        Successfully validated · {currentFile.queries.length}{' '}
                        {currentFile.queries.length === 1 ? 'query' : 'queries'} found
                      </span>
                    ) : (
                      <>
                        Current file · {currentFile.queries.length}{' '}
                        {currentFile.queries.length === 1 ? 'query' : 'queries'}
                        {uploadedTime && ` · ${uploadedTime}`}
                      </>
                    )}
                  </p>
                </div>

                <QueryFilePreview queryFile={currentFile} maxVisible={5} />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReplaceFile}
                    disabled={disabled}
                  >
                    Replace File
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    disabled={disabled}
                    className="text-destructive hover:text-destructive"
                  >
                    Remove File
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'invalid':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 border border-destructive rounded-lg bg-destructive/5">
              <div className="flex-shrink-0 mt-0.5">
                <XCircle className="h-5 w-5 text-destructive" aria-label="Error" />
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-sm font-medium line-through text-muted-foreground">
                    {currentFile?.filename || 'Unknown file'}
                  </p>
                  <p className="text-sm text-destructive font-medium mt-1">Validation failed</p>
                </div>

                {validationErrors.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Problems found:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                      {validationErrors.slice(0, 5).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {validationErrors.length > 5 && (
                        <li className="text-muted-foreground">
                          ... and {validationErrors.length - 5} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReplaceFile}
                    disabled={disabled}
                  >
                    Try Another File
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={disabled}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Check your YAML file format. See documentation for query file examples.
              </AlertDescription>
            </Alert>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-lg">
          Query Configuration{uploadState === 'empty' && ' (Optional)'}
        </CardTitle>
        <CardDescription>
          {uploadState === 'empty'
            ? 'Upload a YAML file with query definitions for this connector'
            : 'Manage query configuration file'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {renderContent()}
        {error && uploadState !== 'invalid' && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Format upload time for display
 */
const formatUploadTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Uploaded just now';
  } else if (diffMins < 60) {
    return `Uploaded ${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `Uploaded ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    return `Uploaded ${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  }
};
